// src/scenes/ProjectPlanning/MainCalendar2.jsx

import React, { useEffect, useState, useMemo, useRef } from "react";
import FullCalendar from "@fullcalendar/react"; // Must be imported before plugins
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction"; // For selectable dates
import axios from "axios";
import deLocale from "@fullcalendar/core/locales/de"; // Import German locale
import styled from "styled-components"; // For custom styling
import { getToken, base_url } from "../../config/config";

// Tooltip Component
const Tooltip = styled.div`
  position: fixed;
  z-index: 10000;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  font-size: 0.9em;
  width: 500px;
  max-width: calc(100vw - 40px);
  max-height: 80vh;
  overflow-y: auto;
  pointer-events: auto;
  transform: translateX(-50%);
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #ffffff;
    filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
  }

  /* Arrow pointing up when tooltip is below the element */
  &.tooltip-below::before {
    top: -10px;
    border-bottom: 10px solid #ffffff;
    border-top: none;
  }

  /* Arrow pointing down when tooltip is above the element */
  &.tooltip-above::before {
    top: auto;
    bottom: -10px;
    border-top: 10px solid #ffffff;
    border-bottom: none;
  }

  .tooltip-header {
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid #4cae50;
    font-size: 1.2em;
    line-height: 1.3;
  }

  .tooltip-badges {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 12px;
  }

  .tooltip-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.8em;
    font-weight: bold;
    color: white;
    text-transform: uppercase;
  }

  .tooltip-process {
    background-color: #3498db;
  }

  .tooltip-task {
    background-color: #e74c3c;
  }

  .tooltip-anr {
    background-color: #f39c12;
    color: white;
    padding: 4px 12px;
    border-radius: 15px;
    font-size: 0.9em;
    font-weight: bold;
  }

  .tooltip-content {
    line-height: 1.4;
    color: #495057;
  }

  .tooltip-section {
    margin-bottom: 16px;
    padding: 12px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .tooltip-section-title {
    font-weight: bold;
    color: #495057;
    font-size: 1em;
    margin-bottom: 8px;
    padding-bottom: 4px;
    border-bottom: 1px solid #dee2e6;
  }

  .tooltip-row {
    margin-bottom: 6px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  .tooltip-label {
    font-weight: 500;
    color: #6c757d;
    margin-right: 8px;
    min-width: 80px;
    flex-shrink: 0;
  }

  .tooltip-value {
    color: #2c3e50;
    flex: 1;
    text-align: right;
    word-break: break-word;
  }

  .tooltip-time {
    background-color: #e8f5e9;
    color: #2e7d32;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
  }

  .tooltip-time-pending {
    background-color: #fff3e0;
    color: #ef6c00;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
  }

  .tooltip-time-unavailable {
    background-color: #ffebee;
    color: #c62828;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
  }

  .tooltip-hours {
    background-color: #e8f5e9;
    color: #2e7d32;
    padding: 2px 8px;
    border-radius: 4px;
    margin-left: 8px;
    font-weight: 500;
  }

  .tooltip-project-info {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 12px;
  }

  .tooltip-project-title {
    color: #856404;
    font-weight: bold;
    margin-bottom: 6px;
  }

  .tooltip-project-content {
    color: #6c5700;
    font-weight: 500;
  }

  .tooltip-details {
    background-color: #e8f4fd;
    border: 1px solid #bee5eb;
    padding: 12px;
    border-radius: 8px;
  }

  .tooltip-details-title {
    color: #0c5460;
    font-weight: bold;
    margin-bottom: 8px;
  }

  .tooltip-details-content {
    font-size: 0.8em;
    background-color: #ffffff;
    padding: 8px;
    border-radius: 4px;
    border: 1px solid #d1ecf1;
    color: #0c5460;
    max-height: 120px;
    overflow-y: auto;
    white-space: pre-wrap;
    font-family: monospace;
  }

  .tooltip-image {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 12px;
    border: 2px solid #e9ecef;
  }
`;

// Define your process definitions with allocated hours
const processDefinitions = {
  processes: [
    {
      id: 1,
      label:
        "Materialeingang / Materialausgang mit Kontrolle und Weitergabe an die Produktion incl. Auftragsnummer",
      names: ["Abdul Wasay"],
      color: "#005B96",
      allocatedHours: 8, // Example allocated hours
    },
    {
      id: 2,
      label: "Baugruppe montieren",
      names: ["Abdul Wasay"],
      color: "#FF9800",
      allocatedHours: 10, // Example allocated hours
    },
    {
      id: 3,
      label: "Maschine einrichten",
      names: ["Abdul Wasay"],
      color: "#009688",
      allocatedHours: 5, // Example allocated hours
    },
  ],
  availableProcesses: [
    {
      id: 4,
      label: "CNC Fräsen",
      color: "#1FD655",
      allocatedHours: 12, // Example allocated hours
    },
    // ... (additional processes)
  ],
};

// Helper function to calculate hours based on start and end times
const calculateHours = (startTime, endTime) => {
  if (!startTime) return 0;
  if (endTime === "pending") return 0;

  const [startHours, startMinutes, startSeconds] = startTime
    .split(":")
    .map(Number);
  const [endHours, endMinutes, endSeconds] = endTime.split(":").map(Number);

  const startDate = new Date();
  startDate.setHours(startHours, startMinutes, startSeconds);

  const endDate = new Date();
  endDate.setHours(endHours, endMinutes, endSeconds);

  let diff = (endDate - startDate) / (1000 * 60 * 60); // Difference in hours

  if (diff < 0) diff += 24; // Handle overnight shifts

  return diff;
};

// Main Calendar Component
const MainCalendar2 = () => {
  const [events, setEvents] = useState([]);
  const [planningData, setPlanningData] = useState(null);
  const [userProjectData, setUserProjectData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null); // State for hovered event
  const [isTooltipHovered, setIsTooltipHovered] = useState(false); // State for tooltip hover
  
  // Tooltip states
  const [tooltip, setTooltip] = useState({
    visible: false,
    content: null,
    x: 0,
    y: 0,
    position: 'above'
  });
  const tooltipRef = useRef(null);

  // Create a mapping of labels to colors using useMemo for optimization
  const labelColorMap = useMemo(() => {
    const map = {};
    const allProcesses = [
      ...processDefinitions.processes,
      ...processDefinitions.availableProcesses,
    ];
    allProcesses.forEach((process) => {
      map[process.label.trim().toLowerCase()] = process.color;
    });
    console.log("Label Color Map:", map); // Debugging log
    return map;
  }, []);

  // Create a mapping of ANR to allocated hours
  const anrAllocatedHoursMap = useMemo(() => {
    const map = {};
    const allProcesses = [
      ...processDefinitions.processes,
      ...processDefinitions.availableProcesses,
    ];
    allProcesses.forEach((process) => {
      if (process.anr) {
        map[process.anr] = process.allocatedHours;
      }
    });
    console.log("ANR Allocated Hours Map:", map); // Debugging log
    return map;
  }, []);

  // Load data once when the component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch processes
        const planningResponse = await axios.get(
          `${base_url}/v1/GetProjectPlanning`,
        );
        console.log("Planning Response Data:", planningResponse.data);

        // Fetch tasks
        const tasksResponse = await axios.get(`${base_url}/v1/GetAllTasks`);
        console.log("Tasks Response Data:", tasksResponse.data);

        // Validate and set planningData
        if (
          planningResponse.data.status === 1 &&
          tasksResponse.data.status === 1
        ) {
          const processes = Array.isArray(planningResponse.data.data.processes)
            ? planningResponse.data.data.processes
            : [];

          const tasks = Array.isArray(planningResponse.data.data.tasks)
            ? planningResponse.data.data.tasks
            : [];

          setPlanningData({
            processes,
            tasks,
          });
        } else {
          console.error(
            "Error fetching data:",
            planningResponse.data.message,
            tasksResponse.data.message,
          );
          setError(planningResponse.data.message || tasksResponse.data.message);
          return; // Exit early if fetching failed
        }

        // Extract unique ANRs from planning data
        const uniqueANRs = new Set(
          (planningResponse.data.data.processes || []).map(
            (process) => process.anr,
          ),
        );
        console.log("Unique ANRs:", Array.from(uniqueANRs)); // Debugging log

        // Fetch user project data for each ANR
        const userProjectPromises = Array.from(uniqueANRs).map((anr) =>
          axios
            .get(`${base_url}/v1/getUserProjectbyANR2/${anr}`)
            .then((response) => ({ anr, data: response.data }))
            .catch((err) => {
              console.error(
                `Error fetching user project data for ANR ${anr}:`,
                err,
              );
              return { anr, data: null };
            }),
        );

        const userProjectResults = await Promise.all(userProjectPromises);
        console.log("User Project Results:", userProjectResults); // Debugging log

        // Create a mapping of ANR to its projects
        const userProjectMap = {};
        userProjectResults.forEach((result) => {
          if (result.data && result.data.status === 1) {
            if (result.data.data.userProjects) {
              userProjectMap[result.anr] = {
                userProjects: result.data.data.userProjects,
                projectDetails: result.data.data.projectDetails,
                userProjectsExist: true,
              };
            } else {
              // No user projects; handle minimal data
              userProjectMap[result.anr] = {
                Auftragsbezeichnung:
                  result.data.data.Auftragsbezeichnung ||
                  "Keine Auftragsbezeichnung",
                mainPicture: result.data.data.mainPicture
                  ? result.data.data.mainPicture.s3Url
                  : null,
                userProjectsExist: false,
              };
            }
          } else {
            userProjectMap[result.anr] = {
              userProjectsExist: false,
            };
          }
        });

        console.log("User Project Map:", userProjectMap); // Debugging log

        setUserProjectData(userProjectMap);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Empty dependency array ensures this runs only once

  // Memoize the events to avoid unnecessary recalculations
  const computedEvents = useMemo(() => {
    if (!planningData) return [];

    const processes = planningData.processes || [];
    const tasks = planningData.tasks || [];

    // Debugging: Log the type and content of tasks
    console.log(
      "Type of tasks:",
      Array.isArray(tasks) ? "Array" : typeof tasks,
    );
    console.log("Tasks Data:", tasks);

    // Map processes to calendar events
    const processEvents = processes
      .filter((process) => process.scheduledDate) // Ensure scheduledDate exists
      .map((process) => ({
        id: process.id,
        title: process.anr || "Kein ANR",
        start: new Date(process.scheduledDate),
        allDay: true,
        backgroundColor:
          labelColorMap[process.label.trim().toLowerCase()] || "#1E90FF", // Use mapped color or default
        extendedProps: {
          type: "process",
          label: process.label,
          anr: process.anr,
          names: process.names.join(", ") || "Nicht verfügbar",
          hours: process.hours || "Nicht verfügbar",
          StartTime: process.StartTime || "",
          EndTime: process.EndTime || "ausstehend",
          shouldHighlight: false, // Default to false
          Auftragsbezeichnung: process.auftragsbezeichnung || "",
          mainPicture: userProjectData[process.anr]?.mainPicture || null,
          userProjectsExist:
            userProjectData[process.anr]?.userProjectsExist || false,
          projectDetails: userProjectData[process.anr]?.projectDetails || null,
        },
      }));

    // Map tasks to calendar events
    const taskEvents = tasks
      .filter((task) => task.date) // Ensure date exists
      .map((task) => ({
        id: task.id, // Use the appropriate identifier
        title: task.anr
          ? `${task.anr}: ${task.description}`
          : task.description || "Kein Titel",
        start: new Date(task.date),
        allDay: true,
        backgroundColor:
          labelColorMap[task.label?.trim().toLowerCase()] || "#32CD32", // Use optional chaining
        extendedProps: {
          type: "task",
          label: task.label || "Keine Bezeichnung",
          anr: task.anr || "Keine ANR",
          assignee: task.assignee || "Nicht verfügbar",
          description: task.description || "Keine Beschreibung",
          hours: task.hours || "Nicht verfügbar",
          StartTime: task.StartTime || "",
          EndTime: task.EndTime || "ausstehend",
          shouldHighlight: false, // Default to false
          Auftragsbezeichnung:
            userProjectData[task.anr]?.Auftragsbezeichnung || "",
          mainPicture: userProjectData[task.anr]?.mainPicture || null,
          userProjectsExist:
            userProjectData[task.anr]?.userProjectsExist || false,
          projectDetails: userProjectData[task.anr]?.projectDetails || null,
        },
      }));

    // Combine all events
    let combinedEvents = [...processEvents, ...taskEvents];

    // Iterate through each process to check if total time exceeds allocated hours
    processes.forEach((process) => {
      const anr = process.anr;
      if (!anr) return;

      const allocatedHours = process.hours || 0;
      const projectInfo = userProjectData[anr] || {};

      if (projectInfo.userProjectsExist) {
        const projects = projectInfo.userProjects || [];

        // Sum total hours from projects
        const totalHours = projects.reduce((sum, project) => {
          return sum + calculateHours(project.StartTime, project.EndTime);
        }, 0);

        if (totalHours > allocatedHours) {
          // Highlight in red
          combinedEvents = combinedEvents.map((event) => {
            if (
              event.extendedProps.anr === anr &&
              event.extendedProps.type === "process"
            ) {
              return {
                ...event,
                backgroundColor: "#FF0000", // Red color
                extendedProps: {
                  ...event.extendedProps,
                  shouldHighlight: true,
                  names:
                    projects.map((p) => p.user).join(", ") ||
                    event.extendedProps.names,
                  hours: totalHours.toFixed(2),
                  projectDetails: projectInfo.projectDetails || null,
                  userProjectsExist: true,
                },
              };
            }
            return event;
          });
        }
      } else {
        // Attach Auftragsbezeichnung and mainPicture if available
        const Auftragsbezeichnung =
          projectInfo.Auftragsbezeichnung || "Keine Auftragsbezeichnung";
        const mainPicture = projectInfo.mainPicture || null;

        combinedEvents = combinedEvents.map((event) => {
          if (
            event.extendedProps.anr === anr &&
            event.extendedProps.type === "process"
          ) {
            return {
              ...event,
              extendedProps: {
                ...event.extendedProps,
                Auftragsbezeichnung,
                mainPicture,
                userProjectsExist: false,
              },
            };
          }
          return event;
        });
      }
    });

    // Add Auftragsbezeichnung and mainPicture to tasks
    // Assuming that tasks also have ANR that correspond to projects
    tasks.forEach((task) => {
      const anr = task.anr;
      if (!anr) return;

      const projectInfo = userProjectData[anr] || {};

      if (!projectInfo.userProjectsExist) {
        const Auftragsbezeichnung =
          projectInfo.Auftragsbezeichnung || "Keine Auftragsbezeichnung";
        const mainPicture = projectInfo.mainPicture || null;

        combinedEvents = combinedEvents.map((event) => {
          if (
            event.extendedProps.anr === anr &&
            event.extendedProps.type === "task"
          ) {
            return {
              ...event,
              extendedProps: {
                ...event.extendedProps,
                Auftragsbezeichnung,
                mainPicture,
                userProjectsExist: false,
              },
            };
          }
          return event;
        });
      }
    });

    console.log("Combined Events:", combinedEvents); // Debugging log

    return combinedEvents;
  }, [planningData, userProjectData, labelColorMap]);

  // Update the events state only when computedEvents changes
  useEffect(() => {
    setEvents(computedEvents);
    console.log("Events Set:", computedEvents); // Debugging log
  }, [computedEvents]);

  if (loading) {
    return <div>Lade Kalender...</div>;
  }

  if (error) {
    return <div>Fehler beim Laden des Kalenders: {error}</div>;
  }

  // Helper function to safely render values
  const safeRender = (value) => {
    if (value === null || value === undefined) {
      return "Nicht verfügbar";
    }
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Function to handle event mouse enter with tooltip
  const handleEventMouseEnter = (info) => {
    setHoveredEvent(info.event);
    
    const rect = info.el.getBoundingClientRect();
    const { extendedProps } = info.event;
    const {
      type,
      label,
      anr,
      names,
      assignee,
      hours,
      description,
      StartTime,
      EndTime,
      Auftragsbezeichnung,
      mainPicture,
      userProjectsExist,
      projectDetails,
    } = extendedProps;

    const isProcess = type === "process";
    const isTask = type === "task";

    // Calculate tooltip positioning to keep it within screen bounds
    const tooltipWidth = 500; // Match the tooltip's width
    const tooltipMaxHeight = window.innerHeight * 0.8; // Max 80% of viewport height
    const margin = 20; // Margin from screen edges
    
    let x = rect.left + rect.width / 2;
    let y = rect.top - 10;
    let tooltipPosition = 'above'; // default position
    
    // Adjust horizontal position if tooltip would go off screen
    if (x + tooltipWidth / 2 > window.innerWidth - margin) {
      x = window.innerWidth - tooltipWidth / 2 - margin;
    }
    if (x - tooltipWidth / 2 < margin) {
      x = tooltipWidth / 2 + margin;
    }
    
    // Adjust vertical position if tooltip would go off screen
    if (y - tooltipMaxHeight < margin) {
      // Position below the event instead of above
      y = rect.bottom + 10;
      tooltipPosition = 'below';
    }
    
    // Ensure tooltip doesn't go below viewport when positioned below
    if (tooltipPosition === 'below' && y + tooltipMaxHeight > window.innerHeight - margin) {
      y = window.innerHeight - tooltipMaxHeight - margin;
    }

    setTooltip({
      visible: true,
      x: x,
      y: y,
      position: tooltipPosition,
      content: (
        <div>
          {/* Header */}
          <div className="tooltip-header">
            {safeRender(info.event.title)}
          </div>
          
          {/* Badges */}
          <div className="tooltip-badges">
            <span className={`tooltip-badge ${isProcess ? 'tooltip-process' : 'tooltip-task'}`}>
              {isProcess ? 'Process' : 'Task'}
            </span>
            {anr && <span className="tooltip-anr">ANR: {safeRender(anr)}</span>}
          </div>

          {/* Main Picture */}
          {mainPicture && (
            <img src={mainPicture} alt="Projektbild" className="tooltip-image" />
          )}
          
          <div className="tooltip-content">
            {/* Basic Information Section */}
            <div className="tooltip-section">
              <div className="tooltip-section-title">Grundinformationen</div>
              {isProcess && (
                <>
                  <div className="tooltip-row">
                    <span className="tooltip-label">Label:</span>
                    <span className="tooltip-value">{safeRender(label)}</span>
                  </div>
                  <div className="tooltip-row">
                    <span className="tooltip-label">Namen:</span>
                    <span className="tooltip-value">{safeRender(names)}</span>
                  </div>
                  <div className="tooltip-row">
                    <span className="tooltip-label">Stunden:</span>
                    <span className="tooltip-value">
                      <span className="tooltip-hours">{safeRender(hours)}</span>
                    </span>
                  </div>
                </>
              )}
              {isTask && (
                <>
                  <div className="tooltip-row">
                    <span className="tooltip-label">Assignee:</span>
                    <span className="tooltip-value">{safeRender(assignee)}</span>
                  </div>
                  <div className="tooltip-row">
                    <span className="tooltip-label">Stunden:</span>
                    <span className="tooltip-value">
                      <span className="tooltip-hours">{safeRender(hours)}</span>
                    </span>
                  </div>
                  <div className="tooltip-row">
                    <span className="tooltip-label">Beschreibung:</span>
                    <span className="tooltip-value">{safeRender(description)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Time Information Section */}
            <div className="tooltip-section">
              <div className="tooltip-section-title">Zeitinformationen</div>
              <div className="tooltip-row">
                <span className="tooltip-label">Startzeit:</span>
                <span className="tooltip-value">
                  <span className={StartTime ? 'tooltip-time' : 'tooltip-time-unavailable'}>
                    {safeRender(StartTime) || "Nicht verfügbar"}
                  </span>
                </span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">Endzeit:</span>
                <span className="tooltip-value">
                  <span className={EndTime && EndTime !== "ausstehend" ? 'tooltip-time' : 'tooltip-time-pending'}>
                    {safeRender(EndTime) || "ausstehend"}
                  </span>
                </span>
              </div>
            </div>

            {/* Project Information Section */}
            {Auftragsbezeichnung && Auftragsbezeichnung !== "Keine Auftragsbezeichnung" && (
              <div className="tooltip-project-info">
                <div className="tooltip-project-title">Projektinformationen</div>
                <div className="tooltip-project-content">
                  <strong>Auftragsbezeichnung:</strong> {safeRender(Auftragsbezeichnung)}
                </div>
              </div>
            )}

            {/* Project Details Section */}
            {/* {userProjectsExist && projectDetails && (
              <div className="tooltip-details">
                <div className="tooltip-details-title">Project Details</div>
                <div className="tooltip-details-content">
                  {safeRender(projectDetails)}
                </div>
              </div>
            )} */}
          </div>
        </div>
      )
    });
  };

  // Function to handle event mouse leave
  const handleEventMouseLeave = () => {
    setHoveredEvent(null);
    // Use a small delay to allow mouse to move to tooltip
    setTimeout(() => {
      if (!isTooltipHovered) {
        setTooltip({
          visible: false,
          content: null,
          x: 0,
          y: 0,
          position: 'above'
        });
      }
    }, 100);
  };

  // Function to handle tooltip mouse enter
  const handleTooltipMouseEnter = () => {
    setIsTooltipHovered(true);
  };

  // Function to handle tooltip mouse leave
  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    setTooltip({
      visible: false,
      content: null,
      x: 0,
      y: 0,
      position: 'above'
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Projektplanung Kalender</h2>
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView='dayGridMonth'
        events={events}
        eventMouseEnter={handleEventMouseEnter} // Handle mouse enter
        eventMouseLeave={handleEventMouseLeave} // Handle mouse leave
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,dayGridWeek,dayGridDay",
        }}
        locale={deLocale} // Set locale to German
        height='auto'
        eventDisplay='block'
      />
      
      {/* Tooltip */}
      {tooltip.visible && (
        <Tooltip
          ref={tooltipRef}
          className={`tooltip-${tooltip.position}`}
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          {tooltip.content}
        </Tooltip>
      )}
    </div>
  );
};

export default MainCalendar2;
